import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Linkedin, Twitter } from 'lucide-react';
import { getPostBySlug, getRecentPosts } from '../data/blog';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  const recentPosts = getRecentPosts(3).filter(p => p.slug !== slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="post-not-found">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-brand-blue mb-4">
            Post Not Found
          </h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog" className="btn-primary">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="blog-post-page">
      {/* Header */}
      <section className="section-padding bg-gradient-to-br from-white via-gray-50 to-brand-turquoise/5">
        <div className="container-custom">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 text-brand-turquoise hover:text-brand-blue mb-8 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Blog</span>
          </button>

          <div className="max-w-3xl">
            <span className="badge-lime inline-block mb-4">{post.category}</span>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-blue leading-tight mb-6" data-testid="post-title">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-500">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(post.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="container-custom -mt-8">
        <div className="aspect-video max-h-96 rounded-2xl overflow-hidden shadow-xl">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
            data-testid="post-image"
          />
        </div>
      </section>

      {/* Content */}
      <section className="section-padding" data-testid="post-content">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main Content */}
            <article className="lg:col-span-8">
              <div className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-brand-blue prose-a:text-brand-turquoise prose-a:no-underline hover:prose-a:underline">
                {post.content.split('\n').map((paragraph, index) => {
                  if (paragraph.startsWith('## ')) {
                    return <h2 key={index} className="mt-8 mb-4">{paragraph.replace('## ', '')}</h2>;
                  }
                  if (paragraph.startsWith('### ')) {
                    return <h3 key={index} className="mt-6 mb-3">{paragraph.replace('### ', '')}</h3>;
                  }
                  if (paragraph.startsWith('- ')) {
                    return <li key={index} className="ml-4">{paragraph.replace('- ', '')}</li>;
                  }
                  if (paragraph.startsWith('1. ') || paragraph.startsWith('2. ') || paragraph.startsWith('3. ') || paragraph.startsWith('4. ') || paragraph.startsWith('5. ')) {
                    return <li key={index} className="ml-4">{paragraph.replace(/^\d+\.\s/, '')}</li>;
                  }
                  if (paragraph.trim()) {
                    return <p key={index} className="text-gray-600 leading-relaxed mb-4">{paragraph}</p>;
                  }
                  return null;
                })}
              </div>

              {/* Share */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-4">
                  <span className="font-medium text-brand-blue">Share this post:</span>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center hover:bg-brand-blue hover:text-white transition-colors"
                    data-testid="share-twitter"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center hover:bg-brand-blue hover:text-white transition-colors"
                    data-testid="share-linkedin"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-28">
                {/* Author Card */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-8" data-testid="author-card">
                  <h4 className="font-heading font-bold text-lg text-brand-blue mb-4">About the Author</h4>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-brand-turquoise/20 flex items-center justify-center">
                      <User className="w-8 h-8 text-brand-turquoise" />
                    </div>
                    <div>
                      <p className="font-bold text-brand-blue">Diana</p>
                      <p className="text-sm text-gray-500">Founder, STEMJECTORY</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Guiding STEAM professionals to design careers with purpose, wellness, and balance.
                  </p>
                </div>

                {/* Related Posts */}
                {recentPosts.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 border border-gray-100">
                    <h4 className="font-heading font-bold text-lg text-brand-blue mb-4">More Posts</h4>
                    <div className="space-y-4">
                      {recentPosts.slice(0, 2).map((relatedPost) => (
                        <Link
                          key={relatedPost.id}
                          to={`/blog/${relatedPost.slug}`}
                          className="block group"
                          data-testid={`related-post-${relatedPost.id}`}
                        >
                          <h5 className="font-medium text-brand-blue group-hover:text-brand-turquoise transition-colors mb-1">
                            {relatedPost.title}
                          </h5>
                          <p className="text-sm text-gray-500">{relatedPost.readTime}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
